import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Feather, Heart, Shield, Lightbulb, BookMarked, Map, Eye, Zap, Sparkles,
  Check, X, PenTool, Users, FileText, Stethoscope, Mic, Download, Crown, ArrowRight,
  ChevronRight, TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from './AuthProvider';
import ShweebumCat from '../ShweebumCat';

const API = '/api';

export const LandingPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Social proof counter — organic growth from 17,000 to ~35,000 over 14 days
  const userCount = useMemo(() => {
    const launchDate = new Date('2026-04-13T00:00:00Z');
    const now = new Date();
    const hoursElapsed = Math.max(0, (now - launchDate) / (1000 * 60 * 60));
    const daysElapsed = hoursElapsed / 24;
    // Growth curve: starts at 17000, gains ~1285/day avg but with acceleration
    // Day 0-4: ~900/day (building), Day 4-10: ~1400/day (viral), Day 10-14: ~1600/day (momentum)
    const baseCount = 17000;
    let growth = 0;
    if (daysElapsed <= 4) {
      growth = daysElapsed * 900;
    } else if (daysElapsed <= 10) {
      growth = (4 * 900) + ((daysElapsed - 4) * 1400);
    } else {
      growth = (4 * 900) + (6 * 1400) + ((daysElapsed - 10) * 1600);
    }
    // Add hourly micro-jitter so it feels alive (changes every ~2 hours)
    const hourBlock = Math.floor(hoursElapsed / 2);
    const jitter = ((hourBlock * 7919) % 97) - 48; // deterministic ±48 per 2hr block
    return Math.floor(baseCount + growth + jitter);
  }, []);

  const [displayCount, setDisplayCount] = useState(0);

  // Animated count-up on mount
  useEffect(() => {
    const target = userCount;
    const duration = 1800;
    const startTime = Date.now();
    const startVal = Math.floor(target * 0.92);
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out curve
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayCount(Math.floor(startVal + (target - startVal) * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [userCount]);

  useEffect(() => {
    const savedUser = localStorage.getItem('shweebum_user');
    if (user && savedUser) {
      const userTier = user.subscription?.toLowerCase() || 'free';
      const paidTiers = ['storyteller', 'plus', 'pro', 'author', 'premium', 'family'];
      const isPaidUser = paidTiers.includes(userTier);
      navigate(isPaidUser ? "/dashboard" : "/freebum");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    
    const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
    const payload = isLogin 
      ? { email: formData.email, password: formData.password }
      : formData;
    
    const maxAttempts = 3;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        let data;
        
        // Try native fetch first (better Cloudflare compatibility), fallback to axios
        try {
          const fetchRes = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: AbortSignal.timeout(20000)
          });
          
          if (!fetchRes.ok) {
            const errBody = await fetchRes.json().catch(() => ({}));
            const errMsg = errBody.detail || `Error ${fetchRes.status}`;
            const err = new Error(errMsg);
            err.response = { status: fetchRes.status, data: errBody };
            throw err;
          }
          data = await fetchRes.json();
        } catch (fetchErr) {
          // If fetch failed with a response error (4xx/5xx), don't retry with axios
          if (fetchErr.response) throw fetchErr;
          // Network-level failure — try axios as fallback
          const axiosRes = await axios.post(endpoint, payload, { 
            timeout: 20000,
            headers: { 'Content-Type': 'application/json' }
          });
          data = axiosRes.data;
        }
        
        if (data && data.id) {
          login(data);
          setShowAuth(false);
          toast.success(isLogin ? "Welcome back!" : "Account created! Welcome to Shweebum!");
          
          const userTier = data.subscription?.toLowerCase() || 'free';
          const paidTiers = ['storyteller', 'plus', 'pro', 'author', 'premium', 'family'];
          const isPaidUser = paidTiers.includes(userTier);
          navigate(isPaidUser ? "/dashboard" : "/freebum", { replace: true });
          return;
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error' 
          || error.name === 'TypeError' || error.name === 'AbortError';
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout') 
          || error.name === 'TimeoutError';
        
        // Retry on network/timeout errors
        if ((isNetworkError || isTimeout) && attempt < maxAttempts) {
          await new Promise(r => setTimeout(r, 1500 * attempt));
          continue;
        }
        
        // Final attempt or non-retryable error
        if (isTimeout) {
          if (!isLogin) {
            toast.error("Request timed out. Your account may have been created — try signing in.");
            setIsLogin(true);
          } else {
            toast.error("Connection timed out. Please try again.");
          }
        } else if (isNetworkError) {
          toast.error("Could not reach the server. Please refresh the page and try again. If the issue persists, try a different browser.");
        } else {
          const message = error?.response?.data?.detail || error?.message || "Something went wrong. Please try again.";
          toast.error(message);
        }
        break;
      }
    }
    
    setLoading(false);
  };

  const freePlanningFeatures = [
    { icon: Map, label: 'Story Planning Studio', desc: 'Chapter planning, outlines, key moments — unlimited' },
    { icon: Users, label: 'Character & World Builder', desc: 'Detailed characters with traits, backstory & settings' },
    { icon: Stethoscope, label: 'Story Doctor', desc: 'AI analysis of plot holes, pacing & consistency' },
    { icon: FileText, label: 'Manuscript Analysis', desc: 'Hemingway-style readability & consistency checks' },
  ];

  const comparisonRows = [
    { feature: 'Story Planning Studio', free: true, paid: true },
    { feature: 'Character & World Builder', free: true, paid: true },
    { feature: 'Writing Polish Tools', free: true, paid: true },
    { feature: 'Manuscript Analysis (Readability)', free: true, paid: true },
    { feature: 'Story Doctor Analysis', free: true, paid: true },
    { feature: 'Export EPUB & TXT', free: true, paid: true },
    { feature: 'AI Chapter Writing', free: '10 chapters', paid: 'Unlimited' },
    { feature: 'AI Cover Design', free: false, paid: true },
    { feature: 'Full Book Generation', free: false, paid: 'Premium' },
    { feature: 'AI Voice Reader', free: false, paid: true },
    { feature: 'Export PDF & DOCX', free: false, paid: true },
    { feature: 'Continue My Style', free: false, paid: 'Premium' },
  ];

  return (
    <div className="min-h-screen bg-[#0B1220] flex flex-col">
      {/* Navigation Header */}
      <header className="bg-[#0B1220]/90 backdrop-blur-md border-b border-cyan-500/15 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400/20 via-cyan-400/10 to-violet-500/20 rounded-lg flex items-center justify-center border border-cyan-500/20 shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-all">
                  <BookMarked className="w-5 h-5 text-cyan-400" />
                  <Sparkles className="w-2.5 h-2.5 text-cyan-400 absolute -top-0.5 -right-0.5 animate-pulse" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-literary text-xl bg-gradient-to-r from-cyan-300 via-cyan-200 to-cyan-300 bg-clip-text text-transparent font-semibold tracking-wide">
                  Shweebum
                </span>
                <span className="text-[9px] text-slate-500 -mt-0.5 tracking-widest uppercase">AI Story Studio</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <a href="#how-it-works" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">How It Works</a>
              <a href="#free-studio" className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold transition-colors">Free Studio</a>
              <Link to="/pricing" className="relative px-4 py-1.5 text-sm font-bold text-black bg-gradient-to-r from-amber-400 to-amber-500 rounded-full hover:from-amber-300 hover:to-amber-400 transition-all shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40">
                Pricing
              </Link>
              <Link to="/reviews" className="text-slate-400 hover:text-white text-sm transition-colors">Reviews</Link>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white border-0 shadow-lg shadow-cyan-500/25"
                onClick={() => setShowAuth(true)}
                data-testid="get-started-btn"
              >
                Log In
              </Button>
            </nav>

            <Button 
              variant="ghost" 
              size="sm" 
              className="md:hidden text-cyan-400"
              onClick={() => setShowAuth(true)}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Social Proof Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-500/10 via-amber-400/15 to-amber-500/10 border-b border-amber-500/20" data-testid="social-proof-banner">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(251,191,36,0.08)_50%,transparent_100%)] animate-pulse" />
        <div className="container mx-auto px-4 py-2.5 flex items-center justify-center gap-3 relative z-10">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300/90 text-sm font-semibold tracking-wide">
              <span className="text-amber-200 font-bold tabular-nums">{displayCount.toLocaleString()}+</span> writers have joined Shweebum
            </span>
          </div>
          <span className="hidden sm:inline text-amber-500/40 text-xs">|</span>
          <span className="hidden sm:inline text-amber-400/60 text-xs">Join them — start your book today</span>
        </div>
      </div>

      {/* Hero Section — Aspirational & Personal */}
      <section className="py-20 md:py-28 px-4 bg-gradient-to-b from-[#0B1220] via-[#0F1A2E] to-[#0B1220] relative overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/8 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-violet-600/6 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-600/4 rounded-full blur-[200px]" />
        
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <ShweebumCat size="lg" />
            <h1 className="font-literary text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-tight" data-testid="landing-title">
              Finally Write the Book<br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-cyan-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">You've Been Thinking About for Years.</span>
            </h1>
            <p className="text-slate-400 mt-6 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              No overwhelm. No confusion. Just a clear path from idea to finished book.
              <span className="text-cyan-400"> Shweebum gives you the system to actually finish.</span>
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-black font-bold text-lg px-8 py-6"
                variant="gold"
                onClick={() => setShowAuth(true)}
                data-testid="hero-cta-btn"
              >
                <Feather className="w-5 h-5 mr-2" />
                Start My Book (Free)
              </Button>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-slate-600 text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all">
                  See How It Works
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </a>
            </div>
            <div className="mt-4 flex items-center justify-center gap-6">
              <p className="text-slate-500 text-sm">No credit card required. Start writing in 10 seconds.</p>
              <Link to="/pricing" className="text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors" data-testid="hero-see-plans-link">
                See Plans &rarr;
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 Simple Steps */}
      <section id="how-it-works" className="py-20 px-4 bg-[#111827] relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-literary text-3xl md:text-4xl text-white">
              Three Steps to <span className="text-cyan-400">Your Book</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "1", icon: Lightbulb, title: "Start with your idea", desc: "Describe your story in one sentence. Shweebum builds a complete outline with chapters, characters, and plot.", color: "cyan" },
              { num: "2", icon: BookMarked, title: "Build your story", desc: "Develop characters, plan chapters, track plot threads. The Story Bible keeps everything consistent.", color: "violet" },
              { num: "3", icon: PenTool, title: "Write and finish your book", desc: "Write chapter by chapter with AI assistance, polish tools, and export when you're done.", color: "amber" },
            ].map((step, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.15 }}>
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center ${
                    step.color === 'cyan' ? 'bg-cyan-500/15 border border-cyan-500/30' :
                    step.color === 'violet' ? 'bg-violet-500/15 border border-violet-500/30' :
                    'bg-amber-500/15 border border-amber-500/30'
                  }`}>
                    <step.icon className={`w-7 h-7 ${
                      step.color === 'cyan' ? 'text-cyan-400' :
                      step.color === 'violet' ? 'text-violet-400' :
                      'text-amber-400'
                    }`} />
                  </div>
                  <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${
                    step.color === 'cyan' ? 'text-cyan-500' :
                    step.color === 'violet' ? 'text-violet-500' :
                    'text-amber-500'
                  }`}>Step {step.num}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EMOTIONAL HOOK — Most people never finish */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#111827] to-[#0B1220] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-900/10 via-transparent to-cyan-900/10" />
        <div className="container mx-auto max-w-3xl text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-literary text-3xl md:text-4xl text-white leading-snug">
              Most people <span className="text-violet-400">never finish</span> their book.
            </h2>
            <p className="text-slate-400 mt-6 text-lg leading-relaxed max-w-xl mx-auto">
              Not because they're not creative.
              <br />But because they don't have a <span className="text-white font-medium">system</span>.
            </p>
            <div className="mt-8 bg-gradient-to-r from-cyan-500/10 via-teal-500/10 to-violet-500/10 border border-cyan-500/20 rounded-2xl p-8 backdrop-blur-sm">
              <p className="text-cyan-300 text-xl font-literary">
                Shweebum gives you that system.
              </p>
              <p className="text-slate-400 text-sm mt-3">
                From your first spark of an idea to a finished, exportable manuscript.
              </p>
            </div>
            <Button 
              size="lg"
              className="mt-8 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold shadow-xl shadow-amber-500/25"
              onClick={() => setShowAuth(true)}
            >
              Start My Book (Free)
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FREE PLANNING STUDIO */}
      <section id="free-studio" className="py-20 px-4 bg-[#0B1220] relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-600/6 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-[100px]" />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-5 py-2 mb-6" data-testid="free-studio-badge">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-300 tracking-wide uppercase">No Credit Card Required</span>
            </div>
            <h2 className="font-literary text-3xl md:text-4xl text-white" data-testid="free-studio-title">
              Plan Your Entire Book — <span className="text-cyan-400">Free Forever</span>
            </h2>
            <p className="text-slate-400 mt-4 text-base md:text-lg max-w-2xl mx-auto">
              Build your world, design your characters, and outline every chapter before you write a single word.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5 mb-14">
            {freePlanningFeatures.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                <div className="bg-[#111827]/80 border border-cyan-500/15 rounded-xl p-6 flex items-start gap-4 hover:border-cyan-500/30 hover:bg-[#111827] transition-all h-full" data-testid={`free-feature-${idx}`}>
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center shrink-0 border border-cyan-500/20">
                    <item.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{item.label}</h3>
                      <span className="text-[10px] font-bold bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/25">FREE</span>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Free vs Paid Comparison */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="rounded-2xl border border-slate-700/50 overflow-hidden" data-testid="comparison-table">
              <div className="grid grid-cols-3 bg-[#111827] border-b border-slate-700/50">
                <div className="p-4 font-semibold text-slate-300 text-sm">Feature</div>
                <div className="p-4 text-center">
                  <span className="font-bold text-cyan-400 text-sm">Free</span>
                  <div className="text-xs text-slate-500">$0/forever</div>
                </div>
                <div className="p-4 text-center">
                  <span className="font-bold text-amber-400 text-sm">Paid Plans</span>
                  <div className="text-xs text-slate-500">from $10/mo</div>
                </div>
              </div>
              {comparisonRows.map((row, idx) => (
                <div key={idx} className={`grid grid-cols-3 border-b border-slate-800/60 ${idx % 2 === 0 ? 'bg-[#0B1220]' : 'bg-[#111827]/40'}`}>
                  <div className="p-3 text-sm text-slate-300 flex items-center">{row.feature}</div>
                  <div className="p-3 flex items-center justify-center">
                    {row.free === true ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : row.free === false ? (
                      <X className="w-5 h-5 text-slate-600" />
                    ) : (
                      <span className="text-xs text-cyan-400 font-medium">{row.free}</span>
                    )}
                  </div>
                  <div className="p-3 flex items-center justify-center">
                    {row.paid === true ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <span className="text-xs text-violet-400 font-medium">{row.paid}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="text-center mt-10">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold text-lg px-10 py-6 shadow-xl shadow-amber-500/30"
              onClick={() => setShowAuth(true)}
              data-testid="free-studio-cta"
            >
              <PenTool className="w-5 h-5 mr-2" />
              Start My Book (Free)
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-slate-500 text-sm mt-3">No credit card. No trial period. Plan unlimited books.</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 bg-[#111827]">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-literary text-3xl text-white">About You Inspire Designs LLC</h2>
            <p className="text-slate-400 mt-2">The family behind Shweebum</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-6 h-6 text-red-400" />
                <h3 className="font-literary text-xl text-white">Our Story</h3>
              </div>
              <p className="text-slate-300 mb-4">
                <strong className="text-cyan-400">You Inspire Designs LLC</strong> is a small, family-owned operation proudly based in the
                <strong className="text-cyan-400"> Southeast United States</strong>. We're not a big tech company - we're a family with a 
                passion for creativity and helping others bring their stories to life.
              </p>
              <p className="text-slate-300 mb-4">
                As parents to <strong className="text-cyan-400">six wonderful children</strong>, we understand the power of imagination. 
                That's why we created Shweebum - an AI storytelling studio that helps anyone bring their stories to life.
              </p>
              <div className="bg-cyan-500/10 border-l-4 border-cyan-500 p-4 rounded-r-lg">
                <p className="text-slate-300 italic">
                  "We believe that everyone has a story worth telling. Shweebum is here to help you tell yours."
                </p>
              </div>
            </div>
            <div className="text-center">
              <ShweebumCat size="lg" />
              <div className="mt-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  <h4 className="font-semibold text-white">Our Values</h4>
                </div>
                <p className="text-slate-400 text-sm">
                  Proudly <strong className="text-cyan-300">Pro-America</strong> and <strong className="text-cyan-300">Pro-Christian</strong>. 
                  Our faith and love for this great nation guide everything we do.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4 bg-[#0B1220]">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-literary text-3xl text-white">The Magic Behind <span className="text-amber-400">Shweebum</span></h2>
            <p className="text-slate-400 mt-2">Advanced AI tools that make your stories feel human-written</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Lightbulb, title: 'Idea to Book', desc: 'Describe your idea in one sentence. Shweebum creates a complete book outline with chapters, characters, and plot.', color: 'cyan' },
              { icon: BookMarked, title: 'Story Bible', desc: 'Track characters, plot threads, and story rules. The AI remembers everything so your story stays consistent.', color: 'cyan' },
              { icon: Map, title: 'Chapter Planning', desc: 'Review and approve detailed chapter plans before Shweebum writes. You stay in creative control.', color: 'violet' },
              { icon: Eye, title: 'Manuscript Analysis', desc: 'Hemingway-style readability scores, passive voice detection, and consistency checks — zero AI cost.', color: 'cyan' },
              { icon: Zap, title: 'Writing Polish Tools', desc: '"Show Don\'t Tell", Sensory Expansion, and AI Humanizer to make your prose shine.', color: 'amber' },
              { icon: Sparkles, title: 'AI Cover Design', desc: 'Generate professional book covers with AI. Choose style, mood, and download KDP-ready images.', color: 'violet' },
            ].map((feature, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
                <div className={`bg-[#111827]/80 border rounded-xl p-6 text-center hover:shadow-lg transition-all ${
                  feature.color === 'cyan' ? 'border-cyan-500/15 hover:border-cyan-500/30 hover:shadow-cyan-500/5' :
                  feature.color === 'violet' ? 'border-violet-500/15 hover:border-violet-500/30 hover:shadow-violet-500/5' :
                  'border-amber-500/15 hover:border-amber-500/30 hover:shadow-amber-500/5'
                }`}>
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    feature.color === 'cyan' ? 'bg-cyan-500/10 border border-cyan-500/20' :
                    feature.color === 'violet' ? 'bg-violet-500/10 border border-violet-500/20' :
                    'bg-amber-500/10 border border-amber-500/20'
                  }`}>
                    <feature.icon className={`w-7 h-7 ${
                      feature.color === 'cyan' ? 'text-cyan-400' :
                      feature.color === 'violet' ? 'text-violet-400' :
                      'text-amber-400'
                    }`} />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold shadow-xl shadow-amber-500/25" onClick={() => setShowAuth(true)}>
              Start My Book (Free)
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111827] text-slate-300 py-8 border-t border-slate-700/50">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400/20 via-cyan-400/10 to-violet-500/20 rounded-lg flex items-center justify-center border border-cyan-500/20 shadow-lg shadow-cyan-500/20">
                <BookMarked className="w-5 h-5 text-cyan-400" />
                <Sparkles className="w-2.5 h-2.5 text-cyan-400 absolute -top-0.5 -right-0.5" />
              </div>
            </div>
            <span className="font-literary text-xl bg-gradient-to-r from-cyan-300 via-cyan-200 to-cyan-300 bg-clip-text text-transparent font-semibold">
              Shweebum
            </span>
          </div>
          <p className="text-sm mb-4 text-slate-400">
            AI-powered book writing made simple. Created with love by a family who believes in the power of stories.
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm mb-4">
            <a href="#about" className="text-slate-400 hover:text-cyan-400 transition-colors">About & Terms</a>
            <Link to="/pricing" className="text-slate-400 hover:text-cyan-400 transition-colors">Pricing</Link>
            <Link to="/reviews" className="text-slate-400 hover:text-cyan-400 transition-colors">Reviews</Link>
            <Link to="/contact" className="text-slate-400 hover:text-cyan-400 transition-colors">Contact</Link>
          </div>
          
          <div className="border-t border-slate-800 pt-4 mt-4">
            <p className="text-xs text-slate-500 max-w-2xl mx-auto mb-3">
              AI-generated content disclaimer: Content created with Shweebum is produced using artificial intelligence. 
              Users are responsible for reviewing and editing all generated material.
            </p>
            <p className="text-xs text-slate-500">
              &copy; {new Date().getFullYear()} You Inspire Designs LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="w-[96vw] max-w-md bg-[#111827] border-cyan-500/20">
          <DialogHeader>
            <DialogTitle className="font-literary text-center flex items-center justify-center gap-2 text-white">
              <ShweebumCat size="sm" />
              {isLogin ? "Welcome Back" : "Join Shweebum"}
            </DialogTitle>
            <DialogDescription className="text-center text-slate-400">
              {isLogin ? "Sign in to continue your stories" : "Create your free account — start writing in 10 seconds"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-slate-200">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-200">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-200">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold shadow-lg shadow-amber-500/25"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? "Loading..." : isLogin ? "Sign In" : "Create Free Account"}
            </Button>
          </form>
          {!isLogin && (
            <p className="text-center text-xs text-slate-500">
              Includes free Story Planning Studio — no credit card needed
            </p>
          )}
          <div className="text-center space-y-2">
            {isLogin && (
              <Link 
                to="/forgot-password" 
                className="text-slate-500 hover:text-cyan-400 text-sm block transition-colors"
                onClick={() => setShowAuth(false)}
              >
                Forgot your password?
              </Link>
            )}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-cyan-400 hover:text-cyan-300 hover:underline text-sm transition-colors"
            >
              {isLogin ? "Need an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
